import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Button, Popconfirm, Tooltip } from "antd";
import { useSelector } from "react-redux";
import {
  DeleteFilled,
  QuestionCircleOutlined,
  EditFilled,
} from "@ant-design/icons";
import { useProjectState } from "../../../context/stateContext";
import organizationsActions from "../../../redux/organizations/actions";
import AdminOrganizationCreateModal from "../../../components/Admin/AdminOrganizationsComponent/AdminOrganizationCreateModal";
import AdminOrganizationEditModal from "../../../components/Admin/AdminOrganizationsComponent/AdminOrganizationEditModal";
import AdminOrganizationCheckVersions from "../../../components/Admin/AdminOrganizationsComponent/AdminOrganizationCheckVersions";
import AdminOrganizationSearch from "../../../components/Admin/AdminOrganizationsComponent/AdminOrganizationSearch";
import PageHeader from "../../../components/Common/PageHeader/PageHeader";
import "./index.css";

const Organizations = () => {
  const dispatch = useDispatch();
  const { getOrganizations, deleteOrganization, getCities } =
    organizationsActions;

  const { organizations, loadingOrganization } = useSelector(
    (state) => state.organizationsReducer
  );
  const [signleOrganization, setSingleOrganization] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const { reloadOrganizations } = useSelector(
    (state) => state.organizationsReducer
  );
  const { setOpenEditOrganizationModal } = useProjectState();

  useEffect(() => {
    dispatch(getOrganizations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadOrganizations]);

  useEffect(() => {
    dispatch(getCities());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueCityFilters = organizations
    ?.map((org) => org.city)
    ?.filter((value, index, array) => array.indexOf(value) === index)
    .map((item) => {
      return {
        text: item,
        value: item,
      };
    });

  const organizationModesFilters = [
    { text: "Деливери", value: 1 },
    { text: "Ордеринг", value: 2 },
    { text: "Ордеринг-Деливери", value: "ordering-delivery" },
    { text: "Роом Сервиз", value: 3 },
    { text: "Плащане", value: 0 },
  ];

  const getOrganizationModes = (organizationMode) => {
    const organizationModeNumber = organizationMode?.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      null
    );

    if (organizationModeNumber === 0) {
      return (
        <Tooltip title="Плащане" color="#ff595e">
          <p className="admin-organizations-table-mode admin-organizations-table-mode-payments">
            П
          </p>
        </Tooltip>
      );
    } else if (organizationModeNumber === 1) {
      return (
        <Tooltip title="Деливери" color="#ffca3a">
          <p className="admin-organizations-table-mode admin-organizations-table-mode-delivery">
            Д
          </p>
        </Tooltip>
      );
    } else if (organizationModeNumber === 2) {
      return (
        <Tooltip title="Ордеринг" color="#8ac926">
          <p className="admin-organizations-table-mode admin-organizations-table-mode-ordering">
            О
          </p>
        </Tooltip>
      );
    } else if (organizationModeNumber === 3 && organizationMode.length < 2) {
      return (
        <Tooltip title="Роом Сервиз" color="#1982c4">
          <p className="admin-organizations-table-mode admin-organizations-table-mode-roomservice">
            Р
          </p>
        </Tooltip>
      );
    } else if (organizationModeNumber === 3 && organizationMode.length === 2) {
      return (
        <Tooltip title="Ордеринг и Деливери" color="#6a4c93">
          <p className="admin-organizations-table-mode admin-organizations-table-mode-ordering-delivery">
            ОД
          </p>
        </Tooltip>
      );
    }
  };

  const columns = [
    {
      title: "Име",
      dataIndex: "name",
      key: "name",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Режим",
      dataIndex: "mode",
      render: (text, record) => (
        <span>{getOrganizationModes(record?.myMenuConfiguration?.modes)}</span>
      ),
      filters: organizationModesFilters,
      onFilter: (value, record) => {
        if (record.myMenuConfiguration.modes.length > 1) {
          return "ordering-delivery".indexOf(value) === 0;
        } else {
          return record.myMenuConfiguration.modes.indexOf(value) === 0;
        }
      },
    },
    {
      title: "Град",
      dataIndex: "city",
      key: "city",
      render: (text) => <span>{text}</span>,
      filters: uniqueCityFilters,
      onFilter: (value, record) => record.city.indexOf(value) === 0,
    },
    {
      title: "Бележка",
      dataIndex: "note",
      key: "note",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Обнови",
      dataIndex: "edit",
      rowKey: "edit",
      render: (text, record) => (
        <Button
          onClick={() => {
            setOpenEditOrganizationModal(true);
            setSingleOrganization(record);
          }}
        >
          <EditFilled />
        </Button>
      ),
    },
    {
      title: "Изтрий",
      dataIndex: "delete",
      rowKey: "delete",
      render: (text, record) => (
        <Popconfirm
          icon={
            <QuestionCircleOutlined
              style={{
                color: "red",
              }}
            />
          }
          title="Сигурен ли сте?"
          onConfirm={() => dispatch(deleteOrganization(record))}
          okText="Да"
          cancelText="Не"
        >
          <Button danger>
            <DeleteFilled />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const saveSearchValue = (data) => {
    setSearchValue(data);
  };

  const searchedOrganizations = organizations?.filter((object) =>
    Object.keys(object).some((property) => {
      if (
        property !== "defaultTouchSaleRestaurantId" &&
        property !== "id" &&
        property !== "myMenuConfiguration" &&
        property !== "myMenuDeliveryConfiguration" &&
        property !== "myMenuOrderingConfiguration" &&
        property !== "myMenuPaymentConfiguration" &&
        property !== "myMenuRoomServiceConfiguration" &&
        property !== "tabletsEnabled"
      ) {
        return String(object[property])
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      }
      return null;
    })
  );

  return (
    <>
      <PageHeader text="Организации" />
      <div className="admin-organization-search-column">
        <AdminOrganizationSearch sendSearchValue={saveSearchValue} />
        <div className="admin-organization-buttons-column">
          <AdminOrganizationCreateModal />
          <AdminOrganizationCheckVersions />
        </div>
      </div>

      <AdminOrganizationEditModal organization={signleOrganization} />
      <Table
        className="admin-orgniztion-table"
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        rowKey="id"
        loading={loadingOrganization}
        dataSource={searchedOrganizations}
        columns={columns}
      />
    </>
  );
};

export default Organizations;
